/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ArrayExtensions, getFallbackImageUrl, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { IComponentProps, IGridSettings, IImageSettings, Image, RichTextComponent } from '@msdyn365-commerce/core';
import { ChannelDeliveryOptionConfiguration, SimpleProduct, ProductDeliveryOptions } from '@msdyn365-commerce/retail-proxy';
import { CartLine, ProductCatalog } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classNames from 'classnames';
import * as React from 'react';
import { Quantity } from '../../utilities/quantity';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { PriceComponent } from '../components/price.component';
import { _getScreenSize } from '../../utilities/display-utils';
import { IPickUpInStoreViewProps } from '../../../../modules/dobbies-cart/components/dobbies-cart-pick-up-in-store';
import { ICartState } from '@msdyn365-commerce/global-state';
/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs
 */
export interface ICartlineResourceString {
    /** Display string for discount label */
    discountStringText: string;
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
    /** String for amount key  */
    amountString?: string;
    /** Display string for quantity label  */
    quantityDisplayString: string;
    /** Display string for quantity label  */
    inputQuantityAriaLabel: string;
    /** Aria label for the decrement button in quanitity component  */
    decrementButtonAriaLabel?: string;
    /** Aria label for the increment button in quanitity component  */
    incrementButtonAriaLabel?: string;
    /** original text screenreader */
    originalPriceText: string;
    /** current text screenreader  */
    currentPriceText: string;
    /** shipping Charges Text  */
    shippingChargesText: string;
}

interface ICartLineData {
    cartLine: CartLine;
    product?: SimpleProduct;
    cartState?: ICartState;
    catalogs?: ProductCatalog[];
}

export type CartLineDisplayMode = 'DEFAULT' | 'COMPACT';

/**
 * ICartLineProps: The props required to render cartLineitem
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /** The flag to change the quantity component from interactible to static  */
    disableQuantityInteractivity?: boolean;
    /** The primary image url  */
    primaryImageUrl?: string;
    /** The product url  */
    productUrl?: string;
    /** GridSettings for the product image in cartLine  */
    gridSettings: IGridSettings;
    /** ImageSettings for the product image in cartLine  */
    imageSettings: IImageSettings;
    /** Boolean flag to indicate if the item is out of stock  */
    isOutOfStock?: boolean;
    /** Flag to make quantity section editable  */
    isQuantityEditable?: boolean;
    /** Max quantity for line item  */
    maxQuantity?: number;
    /** Current quantity for line item  */
    currentQuantity?: number;
    /** Resource string for the component  */
    resources: ICartlineResourceString;
    /** SalesLine flag  */
    isSalesLine?: boolean;
    /** Error message to show in place of quantity */
    errorMessage?: string;
    /** Display mode to use */
    displayMode?: CartLineDisplayMode;
    /** Inventory information label  */
    inventoryInformationLabel?: string;
    /** Inventory information class name  */
    inventoryLabelClassName?: string;
    /** Flag to show/hide shipping charges for line items  */
    showShippingChargesForLineItems?: boolean;
    /** Boolean flag to indicate if cart state status is ready  */
    isCartStateReady?: boolean;
    /**  Chanel Delivery Option configuration is from api. */
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    /** The telemetry content */
    telemetryContent?: ITelemetryContent;
    /* VSI Customization - START - 23/11/20 */
    /* Flag to hide quantity controls */
    hideQuantityControls?: boolean;
    isOrderConfirmation?: boolean;
    displaySalesLinePrice?: React.ReactNode;
    /* VSI Customization - END */
    /** Quantity onChange callback  */
    quantityOnChange?(cartLine: CartLine, newQuantity: number): void;
    deliveryOptions?: ProductDeliveryOptions[];
    pickUpInStore?: IPickUpInStoreViewProps;
    remove?: React.ReactNode;
    saveForLater?: React.ReactNode;
    addToWishlist?: React.ReactNode | undefined;
    storePickUpDescription?: string;
    lineIndex?: number;
    maxQuantityMsg?: string;
    postCodeRestrictionText?: string;
    restrictedProducts?: number;
    isOrderConirmationView?: boolean;
}

interface IDimensionStrings {
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
    /** String for amount key  */
    amountString?: string;
}

const CartLineItemFunctions = {
    renderProductDimensions: (Product: SimpleProduct, DimensionStrings: IDimensionStrings) => {
        if (!Product || !Product.Dimensions) {
            return [];
        }

        return Product.Dimensions.map(productDimension => {
            if (productDimension.DimensionTypeValue === 1) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions1`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-color'>
                            {DimensionStrings.colorString}
                            <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span>
                        </span>
                    </div>
                );
            }

            // if (productDimension.DimensionTypeValue === 2) {
            // 	return (
            // 		<div key={`${Product.RecordId}ProductDimensions2`} className='msc-cart-line__product-variant-item'>
            // 			<span className='msc-cart-line__product-configuration'>
            // 				{DimensionStrings.configString}
            // 				<span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span>
            // 			</span>
            // 		</div>
            // 	);
            // }

            if (productDimension.DimensionTypeValue === 3) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions3`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-size'>
                            {DimensionStrings.sizeString}
                            <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 4) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions4`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-style'>
                            {Product.IsGiftCard ? DimensionStrings.amountString : DimensionStrings.styleString}
                            <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span>
                        </span>
                    </div>
                );
            }
            return null;
        });
    },
    renderDiscountLines: (props: ICartLineProps) => {
        if (!props.data.cartLine.DiscountLines || props.data.cartLine.DiscountLines.length === 0) {
            return null;
        }

        const discountLines = props.data.cartLine.DiscountLines ?? [];
        const seen = new Set();
        const uniqueDiscountLines = discountLines.filter(line =>
            line.OfferName ? !seen.has(line.OfferName) && seen.add(line.OfferName) : true
        );

        return uniqueDiscountLines.map((discountLine, index) => {
            return (
                <div key={discountLine.OfferId || index} className='msc-cart-line-item-product-discount'>
                    <span className='msc-cart-line__product-savings-label'>{discountLine.OfferName ? discountLine.OfferName : ''}</span>
                    {/* <span className='msc-cart-line__promo-codes'>
                        {discountLine.DiscountCost && (
                            <>
                                <PriceComponent
                                    data={
                                        props.isSalesLine
                                            ? {
                                                  price: {
                                                      // @ts-ignore
                                                      CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                                  }
                                              }
                                            : {
                                                  price: {
                                                      CustomerContextualPrice: discountLine.EffectiveAmount,
                                                      BasePrice: discountLine.EffectiveAmount
                                                  }
                                              }
                                    }
                                    context={props.context}
                                    id={props.id}
                                    typeName={props.typeName}
                                    className='msc-cart-line__discount-value'
                                />
                            </>
                        )}
                    </span>
                    {discountLine && discountLine.Percentage !== undefined && discountLine.Percentage !== 0 && (
                        <span>{` (${discountLine.Percentage}%)`}</span>
                    )} */}
                </div>
            );
        });
    },
    renderInventoryLabel: (props: ICartLineProps) => {
        if (!props.inventoryInformationLabel) {
            return null;
        }
        const inventoryCssName = props.inventoryLabelClassName
            ? `msc-cart-line__product-inventory-label ${props.inventoryLabelClassName}`
            : 'msc-cart-line__product-inventory-label';
        return <span className={inventoryCssName}>{props.inventoryInformationLabel}</span>;
    },
    renderShippingLabel: (props: ICartLineProps) => {
        const pickupDeliveryModeCode = props.context.request.channel && props.context.request.channel.PickupDeliveryModeCode;
        const channelDeliveryOptionConfig = props.channelDeliveryOptionConfig;
        const cartline = props.data.cartLine;
        let hasShippingMethod = false;
        if (channelDeliveryOptionConfig !== undefined) {
            hasShippingMethod =
                !!cartline.DeliveryMode &&
                channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.some(deliveryMode => deliveryMode !== cartline.DeliveryMode)
                    ? true
                    : false;
        } else {
            hasShippingMethod = !!cartline.DeliveryMode && cartline.DeliveryMode !== pickupDeliveryModeCode ? true : false;
        }
        if (!hasShippingMethod) {
            return undefined;
        }

        const shippingChargeLines = (cartline.ChargeLines || []).filter(chargeLine => chargeLine.IsShipping);

        if (shippingChargeLines.length === 0) {
            return undefined;
        }

        const freightFee = shippingChargeLines.reduce((chargeTotal, chargeLine) => {
            return chargeTotal + (chargeLine.CalculatedAmount || 0);
        }, 0);

        const priceComponent = (
            <PriceComponent
                data={{
                    price: {
                        // @ts-ignore
                        CustomerContextualPrice: freightFee
                    }
                }}
                freePriceText={'Free'}
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                className='msc-cart-line__freight-actual'
                quantity={props.data.cartLine.Quantity}
            />
        );
        return (
            <>
                <label className='msc-cart-line__freight-label'>{`${props.resources.shippingChargesText}:`}</label>
                <span className='shipping-value'>{priceComponent}</span>
            </>
        );
    },
    renderOtherCharges: (props: ICartLineProps) => {
        const cartline = props.data.cartLine;
        const otherCharges = cartline.ChargeLines && cartline.ChargeLines.filter(chargeline => !chargeline.IsShipping);

        return (
            (otherCharges &&
                otherCharges.map((otherCharge, key) => {
                    return otherCharge && otherCharge.CalculatedAmount ? (
                        <div className='msc-cart-line__other-charges' key={key}>
                            <label className='msc-cart-line__other-charges-label'>{`${otherCharge.Description}:`}</label>
                            <span className='other-charge-value'>
                                <PriceComponent
                                    data={{
                                        price: {
                                            // @ts-ignore
                                            CustomerContextualPrice: otherCharge.CalculatedAmount
                                        }
                                    }}
                                    context={props.context}
                                    id={props.id}
                                    typeName={props.typeName}
                                    className='msc-cart-line__other-charges-actual'
                                    quantity={props.data.cartLine.Quantity}
                                />
                            </span>
                        </div>
                    ) : (
                        ''
                    );
                })) ||
            undefined
        );
    }
};

// tslint:disable-next-line:max-func-body-length
const DobbiesCartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const { productUrl, resources, isCartStateReady, isOrderConfirmation, displaySalesLinePrice } = props;
    const { product, cartLine } = props.data;
    // const [deliveryOptions, setDeliveryOption] = React.useState<ProductDeliveryOptions[]>([]);
    const destructDimensionStrings = {
        sizeString: resources.sizeString,
        colorString: resources.colorString,
        styleString: resources.styleString,
        configString: resources.configString,
        amountString: resources.amountString
    };
    const fallbackImageUrl = product && getFallbackImageUrl(cartLine.ItemId, props.context.actionContext.requestContext.apiSettings);
    const imageSettings = props.imageSettings;
    imageSettings.cropFocalRegion = !ObjectExtensions.isNullOrUndefined(imageSettings);

    const productDimensions = product && CartLineItemFunctions.renderProductDimensions(product, destructDimensionStrings);
    const renderDisountLines = CartLineItemFunctions.renderDiscountLines(props);
    // const renderInventoryLabel = CartLineItemFunctions.renderInventoryLabel(props);
    const renderShippingLabel = CartLineItemFunctions.renderShippingLabel(props);
    // const renderOtherCharges = CartLineItemFunctions.renderOtherCharges(props);
    const payLoad = getPayloadObject('click', props.telemetryContent!, '', product?.RecordId.toString());
    const prodAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);
    const productName = product?.Name || cartLine.Description;
    const [screenSize, setScreenSize] = React.useState(1);
    React.useEffect(() => {
        const displaySize = _getScreenSize(props.context);
        setScreenSize(displaySize);
    }, []);

    const _generateErrorMessage = (): JSX.Element | null => {
        if (props.errorMessage) {
            return (
                <div className='msc-alert__header'>
                    <span className='msi-exclamation-triangle' />
                    <span>{props.errorMessage}</span>
                </div>
            );
        }

        return null;
    };
    /* VSI Customization - START - 16/11/20 */
    const onChange = (newValue: number) => {
        if (props.quantityOnChange) {
            props.quantityOnChange(props.data.cartLine, newValue);
        }
    };
    /* VSI Customization - END */
    const _generateQuantityAndPrice = (): JSX.Element[] | null => {
        const nodes = [];

        const errorMessage = _generateErrorMessage();
        // const salePrice = props.data.cartLine.NetAmount + props.data.cartLine.TaxAmount;
        if (props.isOutOfStock) {
            if (cartLine.IsGiftCardLine) {
                return null;
            }
            if (errorMessage) {
                return [errorMessage];
            }
        }
        let savedPrice = 0;
        const originalPrice =
            props.data.cartLine.Quantity && props.data.cartLine.Price ? props.data.cartLine.Quantity * props.data.cartLine.Price : 0;
        const discountedPrice = props.data.cartLine.ExtendedPrice ? props.data.cartLine.ExtendedPrice : 0;
        savedPrice = originalPrice - discountedPrice;
        const savedPriceTxt = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currencyDisplay: 'symbol',
            currency: 'GBP',
            minimumFractionDigits: 2
        }).format(savedPrice);
        /* VSI Customization - START - 17/10/20 */
        // "You save £6." is a dummy text and value just to proceed with design, needs to replace with actual value and will only show in case of discount
        (isCartStateReady !== true || isOrderConfirmation !== true) &&
            nodes.push(
                <div className='msc-cart-line__product-price'>
                    <PriceComponent
                        data={
                            props.isSalesLine
                                ? {
                                      price: {
                                          // @ts-ignore
                                          CustomerContextualPrice: props.data.cartLine.ExtendedPrice, //salePrice,
                                          BasePrice: props.data.cartLine.Price
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.ExtendedPrice,
                                          BasePrice: originalPrice
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className='discount-value'
                        originalPriceText={resources.originalPriceText}
                        currentPriceText={resources.currentPriceText}
                    />

                    {savedPrice > 0 ? <p className='you-save'>You save {savedPriceTxt}</p> : <></>}
                </div>
            );
        /* VSI Customization - END */
        if (props.isQuantityEditable && !props.data.product?.IsGiftCard) {
            if (errorMessage) {
                nodes.push(errorMessage);
            }
            const linePrice = props.data.cartLine.ExtendedPrice || 0;
            const eachPriceAfterDiscount = linePrice / (props.data.cartLine.Quantity || 0);
            nodes.push(
                /* VSI Customization - START - 16/11/20 */
                /* "£11.99 each" is dummy value, used just to proceed with design. need to repalce with dynamic value */

                <div className='msc-cart-line__product-quantity'>
                    <label className='quantity-label'>
                        {resources.quantityDisplayString} -{' '}
                        {props.context.cultureFormatter.formatCurrency(eachPriceAfterDiscount.toFixed(2))} each
                    </label>
                    <Quantity
                        id={`msc-cart-line__quantity_${props.data.product?.RecordId}`}
                        max={props.maxQuantity || 10}
                        currentCount={props.currentQuantity ?? 1}
                        onChange={onChange}
                        inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                        decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                        incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                        hideQuantityControls={props.hideQuantityControls}
                    />
                </div>

                /* VSI Customization - END */
            );
        } else {
            const eachPriceElem = props.displaySalesLinePrice as React.ReactElement | undefined;
            const cartLineQuantity = props.currentQuantity || 1;
            const eachPrice = eachPriceElem?.props?.children?.props?.data?.price?.CustomerContextualPrice / cartLineQuantity;
            nodes.push(
                <>
                    {isOrderConfirmation && <>{displaySalesLinePrice}</>}

                    <div
                        className={classNames('msc-cart-line__quantity', {
                            'single-quantity': props.data.cartLine.Quantity === 1
                        })}
                    >
                        <label className='quantity-label'>{resources.quantityDisplayString}</label>
                        {errorMessage}
                        <span className='quantity-value'>
                            {props.data.cartLine.Quantity}
                            {isOrderConfirmation && ` - ${props.context.cultureFormatter.formatCurrency(eachPrice.toFixed(2))} each`}
                        </span>
                    </div>
                </>
            );
        }

        return nodes;
    };
    React.useEffect(() => {
        getDeliveryOptionsAsync(
            {
                callerContext: props.context.actionContext,
                queryResultSettings: {}
            },
            [cartLine.ProductId ? cartLine.ProductId : 0]
        )
            .then((deliveryOptions: ProductDeliveryOptions[]) => {
                // setDeliveryOption(deliveryOptions);
            })
            .catch((error: Error) => {
                props.context.actionContext.telemetry.debug(
                    `Failed to get delivery options for product ${cartLine.ProductId}: ${error.message}`
                );
            });
    }, [cartLine.ProductId, props.context.actionContext]);

    const isMaxQuantityReached =
        props.maxQuantity !== undefined && props.currentQuantity !== undefined && props.currentQuantity >= props.maxQuantity;

    if (props.displayMode === 'COMPACT') {
        const reducedDimensions: string = product?.Dimensions
            ? product.Dimensions.reduce<string>((str, productDimension) => {
                  if (productDimension.DimensionValue && productDimension.DimensionValue.Value) {
                      if (str) {
                          return `${str}, ${productDimension.DimensionValue.Value}`;
                      }

                      return `${productDimension.DimensionValue.Value}`;
                  }

                  return str;
              }, '')
            : '';

        return (
            <div className='msc-cart-line msc-cart-line__compact'>
                <div className='msc-cart-line__product-image'>
                    <a href={productUrl}>
                        <Image
                            src={props.primaryImageUrl || ''}
                            fallBackSrc={fallbackImageUrl}
                            altText={productName}
                            gridSettings={props.gridSettings}
                            imageSettings={imageSettings}
                            loadFailureBehavior='empty'
                        />
                    </a>
                </div>
                <div className='msc-cart-line__content'>
                    <a className='msc-cart-line__product-title' href={productUrl} {...prodAttribute}>
                        {productName}
                    </a>
                    {reducedDimensions !== '' ? <div className='msc-cart-line__product-variants'>{reducedDimensions}</div> : ''}
                    {_generateQuantityAndPrice()}
                </div>
            </div>
        );
    }

    if (screenSize !== 1) {
        return (
            <div className='msc-cart-line'>
                <div className='msc-cart-line__product-image'>
                    <a href={productUrl} target={props.isOrderConirmationView ? '_blank' : ''}>
                        <Image
                            src={props.primaryImageUrl || ''}
                            fallBackSrc={fallbackImageUrl}
                            altText={productName}
                            gridSettings={props.gridSettings}
                            imageSettings={imageSettings}
                            loadFailureBehavior='empty'
                        />
                    </a>
                </div>
                <div className='w-100'>
                    <div className='msc-cart-line__content'>
                        <div className='msc-cart-line__product'>
                            <a
                                className='msc-cart-line__product-title'
                                target={props.isOrderConirmationView ? '_blank' : ''}
                                href={productUrl}
                                {...prodAttribute}
                            >
                                {productName}
                            </a>
                            {ArrayExtensions.hasElements(productDimensions) ? (
                                <div className='msc-cart-line__product-variants'>{productDimensions}</div>
                            ) : (
                                ''
                            )}
                            {!props.isOrderConfirmation && renderDisountLines}
                            {props.showShippingChargesForLineItems && <div className='msc-cart-line__freight'>{renderShippingLabel}</div>}
                            {_generateQuantityAndPrice()}
                            {screenSize === 4 || screenSize === 5 ? (
                                <>
                                    {_generateQuantityAndPrice() === null ? <div className='no-quantity'></div> : <></>}
                                    <div className='msc-cart-lines-item__action-links 1476'>
                                        {props.addToWishlist}
                                        {props.saveForLater}
                                        {props.remove}
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}
                            {/* Notification here
                            {props.pickUpInStore?.isBopisSelected ? (
                                <div className='notification-alert notification-alert__yellow store-pickup-desc-notif'>
                                    <div className='notification-alert__icon'></div>
                                    <div className='notification-alert__message'>
                                        <p className='notification-alert__message--paragraph'>
                                            <p className='store-pickup-description'>{props.storePickUpDescription}</p>
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                ''
                            )} */}
                            {isMaxQuantityReached ? (
                                <div className='notification-alert notification-alert__yellow restricted-product'>
                                    <div className='notification-alert__icon'></div>
                                    <div className='notification-alert__message'>
                                        <p className='notification-alert__message--paragraph'>
                                            <div className='restriction-note'>
                                                <RichTextComponent text="You've reached the maximum quantity available." />
                                            </div>
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <></>
                            )}
                            {props.postCodeRestrictionText ? (
                                <div className='notification-alert notification-alert__yellow restricted-product'>
                                    <div className='notification-alert__icon'></div>
                                    <div className='notification-alert__message'>
                                        <p className='notification-alert__message--paragraph'>
                                            <div className='restriction-note'>
                                                <RichTextComponent text={props.postCodeRestrictionText} />
                                            </div>
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <></>
                            )}
                            {screenSize === 3 || screenSize === 2 ? (
                                <div className='msc-cart-lines-item__action-links 1280'>
                                    {props.addToWishlist}
                                    {props.saveForLater}
                                    {props.remove}
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                    {props.pickUpInStore ? (
                        <>
                            <div className={props.pickUpInStore.ContainerProps.className}>{props.pickUpInStore.defaultComponent}</div>
                        </>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className='msc-cart-line'>
                <div className='msc-cart-line__product-image '>
                    <a href={productUrl} target={props.isOrderConirmationView ? '_blank' : ''}>
                        <Image
                            src={props.primaryImageUrl || ''}
                            fallBackSrc={fallbackImageUrl}
                            altText={productName}
                            gridSettings={props.gridSettings}
                            imageSettings={imageSettings}
                            loadFailureBehavior='empty'
                        />
                    </a>
                </div>
                <div className='msc-cart-line__content'>
                    <div className='msc-cart-line__product'>
                        <a
                            className='msc-cart-line__product-title'
                            target={props.isOrderConirmationView ? '_blank' : ''}
                            href={productUrl}
                            {...prodAttribute}
                        >
                            {productName}
                        </a>
                        {ArrayExtensions.hasElements(productDimensions) ? (
                            <div className='msc-cart-line__product-variants'>{productDimensions}</div>
                        ) : (
                            ''
                        )}
                        {!props.isOrderConfirmation && renderDisountLines}
                        {/* renderInventoryLabel */}
                        {props.showShippingChargesForLineItems && <div className='msc-cart-line__freight'>{renderShippingLabel}</div>}
                        {/* renderOtherCharges */}
                        {_generateQuantityAndPrice()}
                        {/* {deliveryOptions[0]?.DeliveryOptions?.map(mode => {
                            const isHD = mode.Code === 'HD';
                            const isPF = mode.Code === 'PF';

                            const estDeliveryDate = getDeliveryDateRange(new Date(), props.context.actionContext, mode.Code || '');
                            if (isHD) {
                                return (
                                    <>
                                        <div>
                                            <p>
                                                Delivery: <strong>{mode.Description}</strong>
                                            </p>
                                            <p>
                                                <strong>You&apos;ll be contacted within 24 hours to select delivery date</strong>
                                            </p>
                                        </div>
                                    </>
                                );
                            }

                            if (isPF) {
                                return (
                                    <div>
                                        <p>
                                            Delivery: <strong>{mode.Description}</strong>
                                        </p>
                                        <p>
                                            Estimated arrival by:{' '}
                                            <strong>
                                                {estDeliveryDate?.maxDeliveryDate && (
                                                    <span className='msc-cart-line__delivery-date'>{estDeliveryDate.maxDeliveryDate}</span>
                                                )}
                                            </strong>
                                        </p>
                                    </div>
                                );
                            }
                            return null; // Skip "Common MOD" or unknown codes
                        })} */}
                    </div>
                </div>
            </div>
            <div className='msc-cart-lines-item__action-links'>
                {props.addToWishlist}
                {props.saveForLater}
                {props.remove}
            </div>
        </>
    );
};

// @ts-ignore
// export const CartLineItemComponent: React.FunctionComponent<ICartLineProps> = msdyn365Commerce.createComponent<ICartline>(
//     'CartLineItem',
//     { component: CartLine, ...CartLineItemFunctions }
// );
export default DobbiesCartLine;
